import * as THREE from 'three';
import { renderComposited } from '../scene/blend-composite.js';
import { updateFlatCam, updateScreenCam } from '../scene/cameras.js';
import { leftPanelDesc } from '../scene/panels.js';
import { calibGrid } from '../scene/calib-grid.js';
import { TONE, cornerCam, flatCam, helpers, renderer, scene } from '../scene/context.js';
import { setLightViz } from '../scene/gizmo.js';
import { fitAllShadows } from '../scene/lighting.js';
import { setCameraObjViz } from '../scene/objects.js';
import { setBezelPass } from '../scene/helpers.js';
import { applyBillboards } from '../scene/renderer.js';
import { holdRendering, requestRender } from '../state/bus.js';
import { S, clamp } from '../state/store.js';

/* RENDERING ONE DELIVERABLE FRAME.

   Extracted from export/png.js, which did all of this inline for the single
   frame it produces. The MP4 exporter needs the identical picture, several
   hundred times — and "identical" has to be structural rather than a promise:
   two copies of this would drift the first time one of them learned about a
   new setting, and the difference would show up as a still that does not
   match the video of the same scene. */

/** The pixel size to render at, honouring supersampling and the card's own
 *  maximum viewport. Returns the note to show when it had to be reduced. */
export function frameSizeFor(resW, resH) {
  const gl = renderer.getContext();
  const maxDim = gl.getParameter(gl.MAX_VIEWPORT_DIMS)[0] || 4096;
  const ss = clamp(Math.round(S.ss) || 1, 1, 4);
  let w = resW * ss, h = resH * ss, note = '';
  if (w > maxDim || h > maxDim) {
    const k = Math.min(maxDim / w, maxDim / h);
    w = Math.floor(w * k); h = Math.floor(h * k);
    note = w < resW
      ? ` This card tops out at ${maxDim} px, so the render was smaller than the target and had to be scaled up.`
      : ` Supersampling was reduced to fit this card's ${maxDim} px limit.`;
  }
  return { w, h, ss, note };
}
export const frameSize = () => frameSizeFor(S.res.w, S.res.h);

/* Repeated halving beats one big drawImage — a single 4:1 downscale skips most
   source pixels in some browsers, which is exactly the aliasing we're removing. */
function downscale(src, tw, th) {
  let cur = src;
  while (cur.width >= tw * 2 && cur.height >= th * 2) {
    const c = document.createElement('canvas');
    c.width = Math.max(tw, Math.floor(cur.width / 2));
    c.height = Math.max(th, Math.floor(cur.height / 2));
    const x = c.getContext('2d');
    x.imageSmoothingEnabled = true; x.imageSmoothingQuality = 'high';
    x.drawImage(cur, 0, 0, c.width, c.height);
    cur = c;
  }
  if (cur.width === tw && cur.height === th) return cur;
  const out = document.createElement('canvas');
  out.width = tw; out.height = th;
  const x = out.getContext('2d');
  x.imageSmoothingEnabled = true; x.imageSmoothingQuality = 'high';
  x.drawImage(cur, 0, 0, tw, th);
  return out;
}

/**
 * Put the renderer into export configuration and keep what it takes to undo
 * that. Everything in here is a thing that must NOT reach the deliverable —
 * the helper grid, the lamp bodies, the viewer camera — or a setting the
 * preview is allowed to differ on.
 *
 * AN EXPORT MUST NOT DEPEND ON WHICH VIEW IT WAS STARTED FROM. The preview
 * sets per-view state on the one shared renderer as it draws each pane, and
 * anything an export reads without setting it here is whatever the last pane
 * left behind — which is how an export from Split once came out with black
 * margins where the same export from On-site had the photograph. So every
 * piece of per-view state an export depends on is SET here, not assumed; a new
 * piece of per-view state in scene/renderer.js belongs on this list too.
 *
 * @returns {{w:number,h:number,note:string,end:()=>void}}
 */
export function beginFrames() {
  const { w, h, note } = frameSize();
  const prevPR = renderer.getPixelRatio();
  const prevSize = new THREE.Vector2(); renderer.getSize(prevSize);

  renderer.setPixelRatio(1);
  renderer.setSize(w, h, false);
  fitAllShadows();
  renderer.toneMapping = TONE[S.tone] ?? TONE.none;
  renderer.setClearColor(new THREE.Color(S.bg.color), S.bg.transparent ? 0 : 1);
  helpers.visible = false;   // the gizmo lives in a separate scene, so export skips it
  setLightViz(false);        // lamp bodies must never land in the render
  setCameraObjViz(false);    // nor the viewer camera
  calibGrid.visible = false; // a workspace check on the solve, never the deliverable
  /* The margins between a wall's screens are the physical gap: black. The
     On-site composite switches them to the photograph for its own renders
     (export/onsite.js); the deliverable never shows the building. */
  setBezelPass(false);
  /* The On-site view clips the recess and frame to ONE panel's viewing cone
     when it reaches past the screen edge. An export camera is exactly its own
     panel, so that clip has nothing to remove — and left on, a corner screen's
     second panel was rendered through the first panel's cone. Off for the
     pass; the preview sets its own again when it next draws. */
  const prevClip = renderer.localClippingEnabled;
  renderer.localClippingEnabled = false;

  /* Taken LAST, after the configuration above, and held until end(): the live
     preview must not draw in the gaps an async export leaves between frames,
     or it undoes every line above — see holdRendering in state/bus.js. */
  const release = holdRendering();
  let ended = false;
  return {
    w, h, note,
    /* Safe to call twice — an exporter's finally can call it whether or not
       an earlier path already did. */
    end() {
      if (ended) return;
      ended = true;
      renderer.setPixelRatio(prevPR);
      renderer.setSize(prevSize.x, prevSize.y, false);
      renderer.localClippingEnabled = prevClip;
      release();
      requestRender();
    }
  };
}

/**
 * Render the scene as it stands right now and return it at the deliverable's
 * own resolution. Call between beginFrames() and its end().
 *
 * @param {HTMLCanvasElement} [into]  reuse this canvas instead of allocating.
 *   The video path passes the same one every frame: three hundred discarded
 *   canvases is real memory pressure and a lot of work for the GC in the
 *   middle of an encode.
 * @param {number} [tw] @param {number} [th]  output size, defaulting to the
 *   project's own. The video exporter overrides it because H.264 refuses odd
 *   dimensions and S.res accepts any integer — it is not enough to round the
 *   number used for the filename and the capability check, the pixels have to
 *   actually come out that size or the encoder rejects the first frame.
 */
export function renderFrame(into, tw = S.res.w, th = S.res.h) {
  return renderPanel(leftPanelDesc(), into, tw, th);
}

/**
 * The same picture, for any panel. A corner screen has two, each with its own
 * frustum and its own output size; the single screen is the first of them and
 * goes through renderFrame above, unchanged.
 *
 * The renderer is resized HERE rather than once in beginFrames, because two
 * panels can differ in width. Same order scene/renderer.js's drawSplit uses for
 * its two passes, and for the same reason — one renderer, one canvas.
 */
export function renderPanel(desc, into, tw, th, cam = flatCam) {
  const { w, h } = frameSizeFor(tw, th);
  renderer.setSize(w, h, false);
  applyBillboards(cam);      // face-camera objects face the viewer eye in the export
  updateScreenCam(cam, desc);
  renderComposited(scene, cam);
  const out = downscale(renderer.domElement, tw, th);
  if (!into) return out;
  if (into.width !== out.width || into.height !== out.height) {
    into.width = out.width; into.height = out.height;
  }
  const ctx = into.getContext('2d');
  ctx.clearRect(0, 0, into.width, into.height);
  ctx.drawImage(out, 0, 0);
  return into;
}
